<script setup lang="ts">
import { ref, computed, onMounted, inject } from 'vue';

import { configuratorStateKey } from '../ProductConfiguratorKeys';
import { inputFocusKey } from '../ProductConfiguratorKeys';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import QuantitySelectorRow from './QuantitySelectorRow.vue';

const { configuratorState, updateQuantity } = inject(configuratorStateKey);
const { websiteText } = useWebsiteTexts();
const { inputFocus } = inject(inputFocusKey);

const props = defineProps({
  variants: {
    type: Array,
    required: true
  },
  hasDecoration: {
    type: Boolean,
    default: false
  },
  supportedVariants: {
    type: Array,
    required: true
  },
  selectedDecorationName: {
    type: String,
    default: ''
  },
  isGiftCard: {
    type: Boolean,
    default: false
  }
});

const currentQuantity = computed(
  () => configuratorState.value.quantities || {}
);
const currentQuantityText = computed(() => {
  return `${configuratorState.value.sumQuantity} ${
    websiteText('productdetailspage_printpieceabbreviated').value
  }`;
});

const packageInfoText = computed(() => {
  const firstVariant = props.variants?.[0];
  if (!firstVariant?.packaging?.caption) return '';

  return websiteText('configurator__quantity_info', {
    package: firstVariant.packaging.caption.toLowerCase()
  }).value;
});

function changeQuantity(variantQuantity) {
  updateQuantity(variantQuantity.id, variantQuantity.quantity);
}

function isVariantSupported(variantSystemId) {
  return (
    !props.hasDecoration ||
    props.supportedVariants.indexOf(variantSystemId) !== -1
  );
}
</script>

<template>
  <div class="quantity-selector">
    <div class="quantity-selector__summary">
      <p class="quantity-selector__title">
        {{ websiteText('configurator__choose_quantity').value }}
      </p>
      <p
        :class="[
          'quantity-selector__quantity',
          { 'quantity-selector__quantity--highlighted': inputFocus }
        ]"
      >
        {{ currentQuantityText }}
      </p>
    </div>
    <div class="quantity-selector__content">
      <p v-if="packageInfoText" class="quantity-selector__content-title">
        {{ packageInfoText }}
      </p>
      <QuantitySelectorRow
        v-for="variant in variants"
        :key="variant.systemId"
        :variant="variant"
        :has-decoration="hasDecoration"
        :quantity="currentQuantity[variant.systemId]"
        :variantSupported="isVariantSupported(variant.systemId)"
        :selectedDecorationName="props.selectedDecorationName"
        :isGiftCard="isGiftCard"
        @update-quantity="changeQuantity"
      >
      </QuantitySelectorRow>
    </div>
  </div>
</template>

<style>
.quantity-selector {
  padding: 13px 20px 13px;
  border: 1px solid var(--color-neutrals-50);
  border-radius: 3px;
}

.quantity-selector__summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
}

.quantity-selector__title {
  margin: 0;
  font-weight: bold;
}

.quantity-selector__quantity {
  color: var(--color-text-medium);
  margin: 0;
}

.quantity-selector__quantity--highlighted {
  color: var(--color-feedback-information);
}

.quantity-selector__content-title {
  color: var(--color-neutrals-80);
  font-size: 12px;
  line-height: 20px;
  margin: 13px 0 0;
  font-style: italic;
}
</style>
